import { useState } from 'react';
import { Mail, FileDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Modali condivise per l'invio di un documento (fattura proforma, preventivo, …)
// al cliente. Parametriche via `config` → zero duplicazione tra le pagine.
// config: {
//   oggettoLabel, filePrefix, idsKey,
//   emailCliente(doc), nomeCliente(doc), importo(doc),
//   messaggioSingolo(doc), messaggioMultiplo (string con {numero}/{nome_cliente}),
//   singoloOkText(dest), multiOkText(n),
//   inviaCliente(id, payload) -> Promise, inviaClienti(payload) -> Promise,
// }

const fmtEur = (n) => n == null ? '—' : `€ ${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px', borderBottom: '1px solid var(--ds-border)' }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)' }}>{title}</h2>
          <button onClick={onClose} aria-label="Chiudi" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--ds-muted)', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        <div style={{ padding: '24px 28px' }}>{children}</div>
      </div>
    </div>
  );
}

// ── Invio singolo al cliente ────────────────────────────────────────────────
export function InvioClienteModal({ documento, config, onClose, onToast }) {
  const { user } = useAuth();
  const aziendaNome = user?.tenant?.nome || '';
  const emailAnagrafica = config.emailCliente(documento) || '';
  const [email, setEmail] = useState(emailAnagrafica);
  const [salvaEmail, setSalvaEmail] = useState(true);
  const [oggetto, setOggetto] = useState(`${config.oggettoLabel}${documento.numero ? ` ${documento.numero}` : ''}${aziendaNome ? ` — ${aziendaNome}` : ''}`);
  const [messaggio, setMessaggio] = useState(config.messaggioSingolo(documento));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const emailNuova = email.trim() && email.trim() !== emailAnagrafica;
  const nomeCliente = config.nomeCliente(documento);
  const numeroSafe = (documento.numero || 'documento').replace(/\//g, '-');

  const inp = { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 };

  const invia = async () => {
    if (!email.trim()) { setError('Inserisci un indirizzo email per inviare.'); return; }
    setSending(true); setError(null);
    try {
      const { data } = await config.inviaCliente(documento.id, {
        email: email.trim(),
        oggetto: oggetto.trim(),
        messaggio: messaggio.trim(),
        salva_email: emailNuova && salvaEmail,
      });
      const base = config.singoloOkText(data.destinatario);
      onToast({ type: 'success', text: data.warning ? `${base}. ${data.warning}` : `${base} ✅` });
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Errore nell\'invio al cliente');
    } finally { setSending(false); }
  };

  return (
    <Modal title="Invia al cliente" onClose={() => !sending && onClose()}>
      {!emailAnagrafica && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 'var(--radius-base)', background: tint('var(--warn)', 12), border: '1px solid var(--warn)', color: 'var(--ink)', fontSize: 13 }}>
          {nomeCliente} non ha un'email in anagrafica. Inseriscila qui sotto per inviare.
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>A:</label>
        <input type="email" style={inp} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@cliente.it" />
      </div>
      {emailNuova && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13, color: 'var(--ds-text)', cursor: 'pointer' }}>
          <input type="checkbox" checked={salvaEmail} onChange={(e) => setSalvaEmail(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--brand)', cursor: 'pointer' }} />
          Salva questa email nell'anagrafica del cliente
        </label>
      )}
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>Oggetto</label>
        <input style={inp} value={oggetto} onChange={(e) => setOggetto(e.target.value)} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>Messaggio</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: 110 }} value={messaggio} onChange={(e) => setMessaggio(e.target.value)} />
        <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 4 }}>Il saluto «Gentile {nomeCliente},» e i dati di pagamento sono aggiunti automaticamente.</div>
      </div>
      <div style={{ marginBottom: 20, padding: '8px 12px', background: 'var(--chip)', borderRadius: 'var(--radius-base)', fontSize: 12, color: 'var(--ds-muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <FileDown size={14} strokeWidth={2} /> Allegato: {config.filePrefix}_{numeroSafe}.pdf
      </div>
      {error && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13, background: tint('var(--ds-danger)', 10), border: '1px solid var(--ds-danger)', color: 'var(--ds-danger)' }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button disabled={sending} onClick={onClose} style={{ padding: '10px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
        <button disabled={sending} onClick={invia} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: sending ? 'wait' : 'pointer', fontSize: 14, fontWeight: 500 }}>
          <Mail size={15} strokeWidth={2} /> {sending ? 'Invio…' : 'Invia'}
        </button>
      </div>
    </Modal>
  );
}

// ── Invio multiplo ai clienti ───────────────────────────────────────────────
export function InvioClientiModal({ documenti, config, onClose, onToast, onDone }) {
  const [emailsInline, setEmailsInline] = useState({});
  const [messaggio, setMessaggio] = useState(config.messaggioMultiplo);
  const [salvaEmails, setSalvaEmails] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const resolved = (d) => (emailsInline[d.id] || '').trim() || (config.emailCliente(d) || '');
  const conEmail = documenti.filter((d) => resolved(d));
  const senzaEmail = documenti.filter((d) => !resolved(d));

  const inp = { width: '100%', padding: '7px 10px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 };

  const invia = async () => {
    if (conEmail.length === 0) { setError('Nessun cliente con email: completa almeno un indirizzo per inviare.'); return; }
    setSending(true); setError(null);
    try {
      const emails = {};
      Object.entries(emailsInline).forEach(([id, v]) => { if (v && v.trim()) emails[id] = v.trim(); });
      const { data } = await config.inviaClienti({
        [config.idsKey]: conEmail.map((d) => d.id),
        messaggio: messaggio.trim(),
        emails,
        salva_emails: salvaEmails,
      });
      const saltate = (data.saltate || []).length;
      let text = config.multiOkText(data.inviate);
      if (saltate > 0) text += ` · ${saltate} ${saltate === 1 ? 'saltata' : 'saltate'} (email mancante o errore)`;
      if (data.warning) text += `. ${data.warning}`;
      onToast({ type: data.inviate > 0 ? 'success' : 'error', text: data.inviate > 0 ? `${text} ✅` : text });
      onDone();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Errore nell\'invio ai clienti');
    } finally { setSending(false); }
  };

  return (
    <Modal title="Invia ai clienti" onClose={() => !sending && onClose()}>
      {senzaEmail.length > 0 && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 'var(--radius-base)', background: tint('var(--ds-danger)', 10), border: '1px solid var(--ds-danger)', color: 'var(--ds-danger)', fontSize: 13, fontWeight: 500 }}>
          {senzaEmail.length} {senzaEmail.length === 1 ? 'cliente senza email' : 'clienti senza email'}: completa gli indirizzi qui sotto o {senzaEmail.length === 1 ? 'verrà saltato' : 'verranno saltati'}.
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>Messaggio (applicato a tutti)</label>
        <textarea style={{ ...inp, fontSize: 14, padding: '9px 12px', resize: 'vertical', minHeight: 90 }} value={messaggio} onChange={(e) => setMessaggio(e.target.value)} />
        <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 4 }}>Segnaposto disponibili: <code>{'{numero}'}</code> <code>{'{nome_cliente}'}</code> — sostituiti per ogni documento. Saluto e dati pagamento aggiunti automaticamente.</div>
      </div>
      <div style={{ border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', overflow: 'hidden', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: 'var(--chip)' }}>
            {['Numero', 'Cliente', 'Email', 'Importo'].map((h) => (
              <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--ds-muted)', fontSize: 11 }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {documenti.map((d) => {
              const missing = !resolved(d);
              const emailAnag = config.emailCliente(d) || '';
              return (
                <tr key={d.id} style={{ borderTop: '1px solid var(--ds-border)', background: missing ? tint('var(--ds-danger)', 7) : 'transparent' }}>
                  <td style={{ padding: '9px 12px', color: 'var(--ink)', fontWeight: 500, whiteSpace: 'nowrap' }}>{d.numero}</td>
                  <td style={{ padding: '9px 12px', color: 'var(--ds-muted)' }}>{config.nomeCliente(d)}</td>
                  <td style={{ padding: '9px 12px', minWidth: 180 }}>
                    {emailAnag && !emailsInline[d.id] ? (
                      <span style={{ color: 'var(--ds-text)' }}>{emailAnag}</span>
                    ) : (
                      <input type="email" style={inp} placeholder="email@cliente.it"
                        value={emailsInline[d.id] || ''}
                        onChange={(e) => setEmailsInline((m) => ({ ...m, [d.id]: e.target.value }))} />
                    )}
                  </td>
                  <td style={{ padding: '9px 12px', color: 'var(--ink)', fontWeight: 500, whiteSpace: 'nowrap' }}>{fmtEur(config.importo(d))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, color: 'var(--ds-text)', cursor: 'pointer' }}>
        <input type="checkbox" checked={salvaEmails} onChange={(e) => setSalvaEmails(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--brand)', cursor: 'pointer' }} />
        Salva le email inserite nell'anagrafica dei clienti
      </label>
      {error && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13, background: tint('var(--ds-danger)', 10), border: '1px solid var(--ds-danger)', color: 'var(--ds-danger)' }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
        <span style={{ marginRight: 'auto', fontSize: 12, color: 'var(--ds-muted)' }}>{conEmail.length} {conEmail.length === 1 ? 'verrà inviato' : 'verranno inviati'}</span>
        <button disabled={sending} onClick={onClose} style={{ padding: '10px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
        <button disabled={sending || conEmail.length === 0} onClick={invia} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 'var(--radius-base)', border: 'none', background: conEmail.length === 0 ? 'var(--ds-muted)' : 'var(--brand)', color: '#fff', cursor: sending ? 'wait' : (conEmail.length === 0 ? 'not-allowed' : 'pointer'), fontSize: 14, fontWeight: 500 }}>
          <Mail size={15} strokeWidth={2} /> {sending ? 'Invio…' : `Invia (${conEmail.length})`}
        </button>
      </div>
    </Modal>
  );
}
